import React from "react";

import { css, cx } from "@emotion/css";
import { zodResolver } from "@hookform/resolvers/zod";
import { Resolver, useForm, UseFormReturn } from "react-hook-form";

import { useStyles } from "@/hooks/useStyles.ts";
import { TeleMonitoringCriteria } from "@/models/TelemonitoringCriteriaModel.ts";
import { Queries } from "@/queries/Queries.ts";
import { Form } from "@/uiKit/atoms/forms/Form.tsx";
import {
  CarePlanCreationInfo,
  CarePlanCreationInfoWithPrescription,
  carePlanCreationSchema,
  GestationalNoInsulinCarePlanCreationInfo,
  SimpleCarePlanCreationInfo,
} from "@/uiKit/organisms/PatientCreationAndRenewal/CarePlan/CarePlanCreation.ts";
import { usePatientCreationContext } from "@/uiKit/organisms/PatientCreationAndRenewal/PatientCreationProvider.tsx";

export const CARE_PLAN_CREATION_FORM_ID = "care-plan-creation-info-form";

export type CarePlanCreationFormProps = {
  carePlanInfo?: Partial<CarePlanCreationInfo>;
  onPrescription: (data: CarePlanCreationInfoWithPrescription) => void;
  onCreateNoInsulin: (data: GestationalNoInsulinCarePlanCreationInfo) => void;
  className?: string;
  children?: React.ReactNode | ((formContext: UseFormReturn<SimpleCarePlanCreationInfo>) => React.ReactNode);
};

export const CarePlanCreationForm: React.FC<CarePlanCreationFormProps> = ({
  carePlanInfo,
  onPrescription,
  onCreateNoInsulin,
  children,
  className,
}) => {
  const styles = useStyles(makeStyles);
  const providers = Queries.practitioner.useHSPList({});
  const carePlanDirectives = usePatientCreationContext();
  const defaultProvider = providers.length === 0 ? "other" : providers.length === 1 ? providers[0].hspId : undefined;
  const defaultNurse =
    carePlanDirectives.telemonitoringNurses.length >= 1 ? carePlanDirectives.telemonitoringNurses[0].id : undefined;
  const form = useForm<SimpleCarePlanCreationInfo>({
    resolver: zodResolver(carePlanCreationSchema) as Resolver<SimpleCarePlanCreationInfo>,
    defaultValues: {
      duration: "3",
      homeServiceProviderId: defaultProvider,
      telemonitoringNurseId: defaultNurse,
      ...carePlanInfo,
    },
  });

  const onSubmit = (data: SimpleCarePlanCreationInfo) => {
    if (data.diabetesType === "gestational" && data.insulinScheme === "noInsulin") {
      onCreateNoInsulin(data as GestationalNoInsulinCarePlanCreationInfo);
    } else {
      onPrescription({
        ...data,
        periodStart: carePlanDirectives.carePlanCreationDataDirectives.periodStart,
        inclusionTier: carePlanDirectives.telemonitoringTierMap[data.inclusionCriteria as TeleMonitoringCriteria],
      } as CarePlanCreationInfoWithPrescription);
    }
  };

  return (
    <Form
      id={CARE_PLAN_CREATION_FORM_ID}
      formContext={form}
      onSubmit={onSubmit}
      className={cx(styles.container, className)}
    >
      {typeof children === "function" ? children(form) : children}
    </Form>
  );
};

const makeStyles = () => ({
  container: css({}),
});
